import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState
} from "react";
import { ToastContextType, ToastProps } from "./Toast.types";
import { clearToastManager, setToastManager } from "./ToastManager";

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
	children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
	const [toasts, setToasts] = useState<ToastProps[]>([]);

	const generateId = useCallback(
		() => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
		[]
	);

	const addToast = useCallback(
		(toast: Omit<ToastProps, "id">): string => {
			const id = generateId();
			const newToast: ToastProps = {
				...toast,
				id
			};

			setToasts((prev) => [...prev, newToast]);
			return id;
		},
		[generateId]
	);

	const removeToast = (id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
	};

	const clearAllToasts = () => {
		setToasts([]);
	};

	const success = useCallback(
		(message: string | ReactNode, options?: Partial<ToastProps>): string => {
			return addToast({
				type: "success",
				message,
				...options
			});
		},
		[addToast]
	);

	const error = useCallback(
		(message: string | ReactNode, options?: Partial<ToastProps>): string => {
			return addToast({
				type: "error",
				message,
				...options
			});
		},
		[addToast]
	);

	const warning = useCallback(
		(message: string | ReactNode, options?: Partial<ToastProps>): string => {
			return addToast({
				type: "warning",
				message,
				...options
			});
		},
		[addToast]
	);

	const info = useCallback(
		(message: string | ReactNode, options?: Partial<ToastProps>): string => {
			return addToast({
				type: "info",
				message,
				...options
			});
		},
		[addToast]
	);

	const value: ToastContextType = {
		toasts,
		addToast,
		removeToast,
		clearAllToasts,
		success,
		error,
		warning,
		info
	};

	// Set global toast manager when provider mounts
	useEffect(() => {
		setToastManager({ success, error, warning, info });

		return () => {
			clearToastManager();
		};
	}, [success, error, warning, info]);

	return (
		<ToastContext.Provider value={value}>{children}</ToastContext.Provider>
	);
}

export function useToast(): ToastContextType {
	const context = useContext(ToastContext);
	if (context === undefined) {
		throw new Error("useToast must be used within a ToastProvider");
	}
	return context;
}
